import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { Contest, ContestType, ContestStatus } from '@/mocks/adminContests';
import { getStationContests, contestTypeLabels, contestTypeIcons, contestTypeColors, contestStatusColors, contestStatusLabels, mockWinnerNames, getContestParticipants } from '@/mocks/adminContests';
import ContestCard from './components/ContestCard';
import ContestFilters from './components/ContestFilters';
import ContestFormModal from './components/ContestFormModal';
import ContestDetailModal from './components/ContestDetailModal';
import WinnerPickerModal from './components/WinnerPickerModal';
import HistoryPanel from './components/HistoryPanel';
import NotificationFlowModal from '@/pages/admin/notifications/components/NotificationFlowModal';
import type { NotificationFlowContext } from '@/pages/admin/notifications/components/NotificationFlowModal';

type LoadState = 'loading' | 'ready' | 'error' | 'empty';
type TabActive = 'activos' | 'historial';

export default function AdminContestsPage() {
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [contests, setContests] = useState<Contest[]>([]);
  const [activeTab, setActiveTab] = useState<TabActive>('activos');
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState<ContestType | 'todos'>('todos');
  const [selectedStatus, setSelectedStatus] = useState<ContestStatus | 'todos'>('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContest, setEditingContest] = useState<Contest | null>(null);
  const [detailModal, setDetailModal] = useState<{ open: boolean; contest: Contest | null; mode: 'rules' | 'winners' | 'participants' }>({
    open: false,
    contest: null,
    mode: 'rules',
  });
  const [winnerPicker, setWinnerPicker] = useState<{ open: boolean; contest: Contest | null }>({ open: false, contest: null });
  const [flowModal, setFlowModal] = useState<{ open: boolean; context: NotificationFlowContext }>({
    open: false,
    context: { type: 'concurso', relatedModule: 'contests' },
  });
  const mountedRef = useRef(false);

  const loadData = () => {
    setDataState('loading');
    try {
      const data = getStationContests(1);
      if (!data || data.length === 0) {
        setDataState('empty');
        setContests([]);
      } else {
        setContests(data);
        setDataState('ready');
      }
    } catch {
      setDataState('error');
    }
  };

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      const timer = setTimeout(() => loadData(), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const retry = () => loadData();

  /* ─── Counters ─── */
  const counts = useMemo(() => {
    const typeCounts: Record<string, number> = {};
    const statusCounts: Record<string, number> = {};
    const types: ContestType[] = ['sorteo', 'trivia', 'comentarios', 'compartir', 'participacion'];
    const statuses: ContestStatus[] = ['activo', 'proximamente', 'pausado', 'finalizado'];

    types.forEach((t) => { typeCounts[t] = contests.filter((c) => c.type === t).length; });
    statuses.forEach((s) => { statusCounts[s] = contests.filter((c) => c.status === s).length; });

    return { todos: contests.length, types: typeCounts, statuses: statusCounts };
  }, [contests]);

  const filteredContests = useMemo(() => {
    const nonFinished = activeTab === 'activos'
      ? contests.filter((c) => c.status !== 'finalizado')
      : contests.filter((c) => c.status === 'finalizado');

    return nonFinished.filter((c) => {
      const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase());
      const matchesType = selectedType === 'todos' || c.type === selectedType;
      const matchesStatus = selectedStatus === 'todos' || c.status === selectedStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [contests, search, selectedType, selectedStatus, activeTab]);

  const activeCount = contests.filter((c) => c.status === 'activo').length;
  const pausedCount = contests.filter((c) => c.status === 'pausado').length;
  const upcomingCount = contests.filter((c) => c.status === 'proximamente').length;
  const finishedCount = contests.filter((c) => c.status === 'finalizado').length;
  const totalParticipants = contests.reduce((sum, c) => sum + c.participants, 0);
  const totalShares = contests.reduce((sum, c) => sum + c.shares, 0);
  const draftCount = contests.filter((c) => !c.published).length;
  const winnersCount = contests.filter((c) => c.winnerName).length;

  /* ─── Handlers ─── */
  const handleSave = (data: Partial<Contest>) => {
    if (data.id && contests.find((c) => c.id === data.id)) {
      setContests((prev) => prev.map((c) => (c.id === data.id ? { ...c, ...data } as Contest : c)));
    } else {
      setContests((prev) => [
        { ...data, id: Date.now(), stationId: 1, participants: 0 } as Contest,
        ...prev,
      ]);
    }
    if (dataState === 'empty') setDataState('ready');
  };

  const handleDelete = async (contest: Contest) => {
    const result = await SwalAlert.confirm(
      '¿Eliminar concurso?',
      `Se eliminará permanentemente "${contest.title}". Esta acción no se puede deshacer.`,
    );
    if (result.isConfirmed) {
      setContests((prev) => {
        const next = prev.filter((c) => c.id !== contest.id);
        if (next.length === 0) setDataState('empty');
        return next;
      });
      await SwalAlert.toast('Concurso eliminado.', 'success');
    }
  };

  const handleActivate = async (contest: Contest) => {
    const result = await SwalAlert.confirm(
      '¿Activar concurso?',
      `"${contest.title}" volverá a estar activo y los oyentes podrán participar de nuevo.`,
    );
    if (result.isConfirmed) {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, status: 'activo' as ContestStatus } : c)));
      await SwalAlert.toast('Concurso activado.', 'success');
    }
  };

  const handlePause = async (contest: Contest) => {
    const result = await SwalAlert.confirm(
      '¿Pausar concurso?',
      `"${contest.title}" se pausará. Los oyentes no podrán participar mientras esté pausado.`,
    );
    if (result.isConfirmed) {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, status: 'pausado' as ContestStatus } : c)));
      await SwalAlert.toast('Concurso pausado.', 'success');
    }
  };

  const handleFinalize = async (contest: Contest) => {
    const result = await SwalAlert.confirm(
      '¿Finalizar concurso?',
      `"${contest.title}" se marcará como finalizado. ${!contest.winnerName ? 'Recordá elegir un ganador después.' : ''}`,
    );
    if (result.isConfirmed) {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, status: 'finalizado' as ContestStatus } : c)));
      await SwalAlert.toast('Concurso finalizado.', 'success');
    }
  };

  const handlePickWinner = (contest: Contest) => {
    if (contest.participants === 0) {
      SwalAlert.warning('Sin participantes', 'Este concurso no tiene participantes. Nadie puede ganar.');
      return;
    }
    if (contest.status === 'proximamente') {
      SwalAlert.warning('Aún no empieza', 'No podés elegir un ganador antes de que el concurso esté activo o finalizado.');
      return;
    }
    setWinnerPicker({ open: true, contest });
  };

  const handleConfirmWinner = (contest: Contest, winnerName: string, winnerAvatar: string) => {
    setContests((prev) => prev.map((c) => (c.id === contest.id ? {
      ...c,
      winnerName,
      winnerAvatar,
      status: 'finalizado' as ContestStatus,
    } : c)));
    SwalAlert.success('¡Ganador elegido!', `${winnerName} es el ganador de "${contest.title}".`);

    // Offer to notify
    SwalAlert.confirm(
      '¿Notificar a tus oyentes?',
      `¿Querés anunciar a ${winnerName} como ganador de "${contest.title}" con una notificación push?`,
    ).then((result) => {
      if (result.isConfirmed) {
        setFlowModal({
          open: true,
          context: {
            type: 'concurso',
            relatedModule: 'contests',
            relatedItemId: contest.id,
            relatedItemName: contest.title,
            prefilledTitle: `¡Tenemos ganador! ${winnerName}`,
            prefilledMessage: `${winnerName} es el ganador de "${contest.title}". ¡Felicitaciones! Gracias a todos por participar.`,
          },
        });
      }
    });
  };

  const handleShare = async (contest: Contest) => {
    const shareUrl = `https://radioelnorte.com/contests/${contest.id}`;
    const result = await SwalAlert.fire({
      title: 'Compartir concurso',
      html: `
        <div class="text-left space-y-3">
          <p class="text-foreground-600 text-xs">Copiá el enlace o compartí directamente:</p>
          <div class="flex items-center gap-2 p-2.5 rounded-lg bg-background-100 border border-background-200">
            <code class="text-foreground-700 text-[11px] flex-1 break-all select-all">${shareUrl}</code>
          </div>
          <div class="grid grid-cols-4 gap-2">
            <button onclick="navigator.clipboard.writeText('${shareUrl}'); Swal.close();" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200">
              <i class="ri-link text-sky-500 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">Copiar</span>
            </button>
            <a href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}" target="_blank" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200 no-underline">
              <i class="ri-facebook-fill text-sky-600 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">Facebook</span>
            </a>
            <a href="https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(contest.title)}" target="_blank" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200 no-underline">
              <i class="ri-twitter-x-fill text-foreground-700 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">X</span>
            </a>
            <a href="https://wa.me/?text=${encodeURIComponent(contest.title + ' ' + shareUrl)}" target="_blank" class="flex flex-col items-center gap-1 p-2.5 rounded-xl bg-background-100 hover:bg-background-200 cursor-pointer transition-colors border border-background-200 no-underline">
              <i class="ri-whatsapp-fill text-emerald-600 text-lg"></i>
              <span class="text-[9px] text-foreground-600 font-semibold">WhatsApp</span>
            </a>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: { popup: 'rounded-2xl border border-background-200/70' },
    });
    if (result.isDismissed) {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, shares: c.shares + 1 } : c)));
    }
  };

  const handlePublish = async (contest: Contest) => {
    const result = await SwalAlert.confirm(
      '¿Publicar concurso?',
      `"${contest.title}" será visible para todos los oyentes en la página pública de la radio.`,
    );
    if (result.isConfirmed) {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, published: true } : c)));
      await SwalAlert.success('¡Concurso publicado!', `"${contest.title}" ya está disponible para tu audiencia.`);
    }
  };

  const handlePublishWinner = async (contest: Contest) => {
    if (!contest.winnerName) {
      await SwalAlert.warning('Sin ganador', 'Primero debés elegir un ganador antes de publicarlo.');
      return;
    }
    const result = await SwalAlert.confirm(
      '¿Publicar ganador?',
      `Se anunciará públicamente que ${contest.winnerName} es el ganador de "${contest.title}".`,
    );
    if (result.isConfirmed) {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, winnerPublished: true } : c)));
      await SwalAlert.success('¡Ganador publicado!', `${contest.winnerName} fue anunciado como ganador de "${contest.title}".`);
    }
  };

  const handleExportParticipants = async (contest: Contest) => {
    if (contest.participants === 0) {
      await SwalAlert.warning('Sin participantes', 'Este concurso no tiene participantes para exportar.');
      return;
    }
    const participants = getContestParticipants(contest.id);
    const header = 'Nombre,Entrada,Fecha';
    const rows = participants.map((p) => `"${p.name}","${p.entry.replace(/"/g, '""')}","${new Date(p.joinedAt).toLocaleDateString('es-MX')}"`);
    const csv = [header, ...rows].join('\n');
    const result = await SwalAlert.fire({
      title: 'Exportar participantes',
      html: `
        <div class="text-left space-y-3">
          <p class="text-foreground-600 text-xs">${participants.length} de ${contest.participants.toLocaleString()} participantes de "${contest.title}":</p>
          <div class="max-h-48 overflow-y-auto rounded-lg border border-background-200">
            <textarea readonly class="w-full h-full min-h-[120px] p-3 text-[11px] text-foreground-700 bg-background-100 border-none outline-none resize-none font-mono">${csv.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/&/g, '&amp;').replace(/"/g, '&quot;')}</textarea>
          </div>
          <p class="text-foreground-400 text-[10px]">Copiá el contenido y pegalo en Excel o Google Sheets.</p>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: 'Copiar CSV',
      showCancelButton: true,
      cancelButtonText: 'Cerrar',
      customClass: {
        popup: 'rounded-2xl border border-background-200/70',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-primary-500 text-white hover:bg-primary-600',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600 hover:bg-background-200',
      },
    });
    if (result.isConfirmed) {
      try {
        await navigator.clipboard.writeText(csv);
        await SwalAlert.toast('CSV copiado al portapapeles.', 'success');
      } catch {
        await SwalAlert.toast('No se pudo copiar. Seleccioná el texto manualmente.', 'warning');
      }
    }
  };

  const openEdit = (contest: Contest) => { setEditingContest(contest); setModalOpen(true); };
  const openNew = () => { setEditingContest(null); setModalOpen(true); };
  const openRules = (contest: Contest) => setDetailModal({ open: true, contest, mode: 'rules' });
  const openWinners = (contest: Contest) => setDetailModal({ open: true, contest, mode: 'winners' });
  const openParticipants = (contest: Contest) => setDetailModal({ open: true, contest, mode: 'participants' });

  /* ─── LOADING STATE ─── */
  if (dataState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-trophy-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Concursos</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-3 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
              <p className="text-foreground-500 text-sm">Cargando concursos…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── ERROR STATE ─── */
  if (dataState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-trophy-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Concursos</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar</h3>
              <p className="text-foreground-500 text-sm mb-5">No se pudieron cargar los concursos. Revisá tu conexión.</p>
              <button onClick={retry} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-refresh-line"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ─── EMPTY STATE ─── */
  if (dataState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-trophy-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Concursos</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-trophy-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Sin concursos</h3>
              <p className="text-foreground-500 text-sm mb-5">Todavía no creaste ningún concurso. ¡Armá el primero y empezá a conectar con tu audiencia!</p>
              <button onClick={openNew} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-add-line text-base"></i>
                Crear primer concurso
              </button>
            </div>
          </main>
        </div>
        <ContestFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditingContest(null); }} onSave={handleSave} contest={editingContest} />
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-background-50/98 backdrop-blur-2xl border-t border-background-200">
          <div className="flex items-center justify-around h-[56px] px-1">
            {[
              { icon: 'ri-dashboard-line', activeIcon: 'ri-dashboard-fill', label: 'Dashboard', active: false, href: '/admin/dashboard' },
              { icon: 'ri-calendar-2-line', activeIcon: 'ri-calendar-2-fill', label: 'Programación', active: false, href: '/admin/schedule' },
              { icon: 'ri-trophy-line', activeIcon: 'ri-trophy-fill', label: 'Concursos', active: true, href: '/admin/contests' },
              { icon: 'ri-broadcast-line', activeIcon: 'ri-broadcast-fill', label: 'Cabina', active: false, href: '/admin/cabina' },
              { icon: 'ri-settings-3-line', activeIcon: 'ri-settings-3-fill', label: 'Ajustes', active: false, href: '/admin/settings' },
            ].map((item) => (
              <Link key={item.label} to={item.href} className={`flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer transition-colors relative ${item.active ? 'text-primary-600' : 'text-foreground-400'}`}>
                <i className={`${item.active ? item.activeIcon : item.icon} text-xl`}></i>
                <span className="text-[10px] font-semibold">{item.label}</span>
                {item.active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary-500 rounded-full"></span>}
              </Link>
            ))}
          </div>
        </nav>
      </div>
    );
  }

  /* ─── READY STATE ─── */
  return (
    <div className="min-h-screen bg-background-100">
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-trophy-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Concursos</h1>
              <p className="text-foreground-400 text-[9px]">{activeCount} activos</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop header */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Concursos y Dinámicas</h2>
              <p className="text-foreground-400 text-xs">
                Sorteos, trivias, comentarios y más. {activeCount} activos, {finishedCount} finalizados.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
              <button onClick={openNew} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-add-line text-base"></i>
                Nuevo concurso
              </button>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <i className="ri-play-circle-line text-emerald-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{activeCount}</p>
                <p className="text-foreground-400 text-[11px]">Activos</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <i className="ri-pause-circle-line text-orange-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{pausedCount}</p>
                <p className="text-foreground-400 text-[11px]">Pausados</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <i className="ri-calendar-todo-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{upcomingCount}</p>
                <p className="text-foreground-400 text-[11px]">Próximos</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
                    <i className="ri-check-double-line text-foreground-500 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{finishedCount}</p>
                <p className="text-foreground-400 text-[11px]">Finalizados</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                    <i className="ri-trophy-line text-amber-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{winnersCount}</p>
                <p className="text-foreground-400 text-[11px]">Ganadores</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-group-line text-primary-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{totalParticipants.toLocaleString()}</p>
                <p className="text-foreground-400 text-[11px]">Participantes</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                    <i className="ri-share-forward-line text-sky-600 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{totalShares.toLocaleString()}</p>
                <p className="text-foreground-400 text-[11px]">Compartidos</p>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-lg bg-foreground-100 flex items-center justify-center">
                    <i className="ri-draft-line text-foreground-500 text-sm"></i>
                  </div>
                </div>
                <p className="text-foreground-950 text-lg font-bold">{draftCount}</p>
                <p className="text-foreground-400 text-[11px]">Borradores</p>
              </div>
            </div>

            {/* Tabs: Activos | Historial */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setActiveTab('activos'); setSelectedType('todos'); setSelectedStatus('todos'); setSearch(''); }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  activeTab === 'activos'
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                }`}
              >
                <i className="ri-play-list-line"></i>
                Concursos activos
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === 'activos' ? 'bg-white/20' : 'bg-background-100'}`}>
                  {activeCount + pausedCount + upcomingCount}
                </span>
              </button>
              <button
                onClick={() => { setActiveTab('historial'); setSelectedType('todos'); setSelectedStatus('todos'); setSearch(''); }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  activeTab === 'historial'
                    ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20'
                    : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
                }`}
              >
                <i className="ri-history-line"></i>
                Historial
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === 'historial' ? 'bg-white/20' : 'bg-background-100'}`}>
                  {finishedCount}
                </span>
              </button>
            </div>

            {/* Filters (only for activos tab) */}
            {activeTab === 'activos' && (
              <ContestFilters
                search={search}
                onSearchChange={setSearch}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
                counts={counts}
              />
            )}

            {/* Mobile create button */}
            {activeTab === 'activos' && (
              <div className="lg:hidden">
                <button onClick={openNew} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-add-line text-base"></i>
                  Nuevo concurso
                </button>
              </div>
            )}

            {/* Content area */}
            {activeTab === 'activos' ? (
              filteredContests.length === 0 ? (
                <div className="bg-background-50 rounded-2xl border border-background-200 p-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                    <i className="ri-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <h3 className="text-foreground-950 font-bold text-base mb-1">Sin resultados</h3>
                  <p className="text-foreground-500 text-sm mb-4">No hay concursos que coincidan con los filtros actuales.</p>
                  <button onClick={() => { setSearch(''); setSelectedType('todos'); setSelectedStatus('todos'); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 cursor-pointer transition-colors">
                    <i className="ri-filter-off-line text-sm"></i>
                    Limpiar filtros
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredContests.map((contest) => (
                    <ContestCard
                      key={contest.id}
                      contest={contest}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onViewRules={openRules}
                      onViewWinners={openWinners}
                      onViewParticipants={openParticipants}
                      onActivate={handleActivate}
                      onPause={handlePause}
                      onFinalize={handleFinalize}
                      onPickWinner={handlePickWinner}
                      onShare={handleShare}
                      onPublish={handlePublish}
                      onPublishWinner={handlePublishWinner}
                      onExportParticipants={handleExportParticipants}
                    />
                  ))}
                </div>
              )
            ) : (
              <HistoryPanel
                contests={contests}
                onViewWinners={openWinners}
                onPublishWinner={handlePublishWinner}
              />
            )}
          </div>
        </main>
      </div>



      {/* Modals */}
      <ContestFormModal open={modalOpen} onClose={() => { setModalOpen(false); setEditingContest(null); }} onSave={handleSave} contest={editingContest} />
      <ContestDetailModal open={detailModal.open} contest={detailModal.contest} mode={detailModal.mode} onClose={() => setDetailModal({ open: false, contest: null, mode: 'rules' })} />
      <WinnerPickerModal open={winnerPicker.open} contest={winnerPicker.contest} onClose={() => setWinnerPicker({ open: false, contest: null })} onConfirmWinner={handleConfirmWinner} />
      <NotificationFlowModal open={flowModal.open} context={flowModal.context} onClose={() => setFlowModal((prev) => ({ ...prev, open: false }))} />
    </div>
  );
}